<template>
	<v-app-bar
		v-if="title"
		class="app-base-header bg-background first-app-bar"
		density="compact"
		flat
		:class="`${headerVariant}-variant`"
	>
		<v-app-bar-title
			class="ao-h1"
		>
			<template v-if="headerVariant === 'default'">
				{{ title }}
				<template v-if="!isProductionMode">
					<span class="ps-6 text-accent">WARNING!!!</span>
					<span class="ps-6 text-red">[DEVELOPMENT]</span>
				</template>
			</template>
			<div
				v-else-if="headerVariant === 'mini'"
				class="d-flex align-center overflow-auto"
			>
				<ao-btn
					:text="title"
					variant="text"
				/>

				<template
					v-if="currentModule"
				>
					<ao-icon
						iconName="chevronsRight"
						class="text-icon600"
					/>
					<!-- <span class="px-2 text-textSecondary700">/</span> -->

					<v-menu
						v-if="currentModule.hasOtherModules"
					>
						<template v-slot:activator="{ props }">
							<ao-btn
								v-bind="props"
								:text="currentModule.title"
								:appendIconName="'arrowDown'"
								variant="text"
							/>
						</template>
						<v-card>
							<ao-btn
								v-for="item in moduleLinks"
								:key="`app-bar-mini-modules-${item.name}`"
								v-bind="item"
							/>
						</v-card>
					</v-menu>
					<ao-btn
						v-else
						:text="currentModule.title"
						variant="text"
					/>
				</template>

				<template
					v-if="currentTabView"
				>
					<ao-icon
						iconName="chevronsRight"
						class="text-icon600"
					/>

					<v-menu
						v-if="currentTabView.hasOtherTabViews"
					>
						<template v-slot:activator="{ props }">
							<ao-btn
								v-bind="props"
								:text="currentTabView.title"
								:prependIconName="currentTabView.icon"
								:appendIconName="'arrowDown'"
								variant="text"
								:loading="!!this.loadedRouterLink"
							/>
						</template>
						<v-card>
							<ao-btn
								v-for="item in tabViewLinks"
								:key="`app-bar-mini-modules-${item.name}`"
								v-bind="item"
							/>
						</v-card>
					</v-menu>
					<ao-btn
						v-else
						variant="text"
						:prependIconName="currentTabView.icon"
						:text="currentTabView.title"
					/>
				</template>
			</div>
		</v-app-bar-title>

		<ao-icon-btn
			size="small"
			:iconName="headerVariant === 'default' ? 'minimize' : 'maximize'"
			color="white"
			class="mr-1"
			@click="changeHeaderVariant"
		/>

		<template v-if="isShowActions">
			<AppHeaderFavorites />
		</template>

	</v-app-bar>

	<v-app-bar
		v-if="headerVariant === 'default' && !!modules.length"
		flat
		density="compact"
		tag="div"
		class="app-bar-modules bg-background"
	>
		<v-slide-group
			show-arrows
			next-icon="mdi-arrow-right-drop-circle"
			prev-icon="mdi-arrow-left-drop-circle"
			class="mx-n1"
		>
			<v-slide-group-item
				v-for="item in moduleLinks"
				:key="`app-bar-modules-${item.name}`"
			>
				<ao-btn
					v-bind="item"
				/>
			</v-slide-group-item>
		</v-slide-group>
	</v-app-bar>

	<v-app-bar
		v-if="headerVariant === 'default' && !!viewTabs.length"
		flat
		density="compact"
		tag="div"
		class="app-bar-view-tabs bg-background app-bar-tabs-block"
		height="48"
	>
		<v-slide-group
			show-arrows
			next-icon="mdi-arrow-right-drop-circle"
			prev-icon="mdi-arrow-left-drop-circle"
			class="mx-n1"
		>
			<v-slide-group-item
				v-for="tab in tabViewLinks"
				:key="`app-bar-tabs-${tab.name}`"
			>
				<ao-btn
					v-bind="tab"
				/>
			</v-slide-group-item>
		</v-slide-group>
	</v-app-bar>
</template>

<script>
import { mapState } from 'pinia'

import eventBus from '@/plugins/eventBus'

import { useUserStore } from '@/stores/userStore'

import AppHeaderFavorites from '@/components/base/AppHeader/AppHeaderFavorites.vue'

import {
	clientStorageLocal,
	LOCAL_STORAGE_APP_HEADER_VARIANT,
} from '@ao/utils'

import { ROUTER_VIEW_VARIANTS_OF_ENTITY_LISTS_CONFIG } from '@/config/router/routerMapConfig'

export default {
	name: 'AppHeader',

	components: {
		AppHeaderFavorites,
	},

	props: {
		isShowActions: {
			type: Boolean,
			default: false,
		},

		title: {
			type: String,
			default: 'OnTrack',
		},

		modules: {
			type: Array,
			default: () => [],
		},

		currentModuleRouteName: {
			type: String,
			default: null,
		},

		tabRoutes: {
			type: Array,
			default: () => [],
		},

		currentViewRouteName: {
			type: String,
			default: null,
		},
	},

	data: () => ({
		headerVariant: 'default', // default | mini

		viewTabsConfig: ROUTER_VIEW_VARIANTS_OF_ENTITY_LISTS_CONFIG,

		loadedRouterLink: null,
	}),

	computed: {
		...mapState(useUserStore, ['selectedCompanyId']),

		viewTabs() {
			let tabs = []
			this.tabRoutes.forEach((tab) => {
				let tabType = this.getTabType(tab.path)
				tabs.push({
					routeName: tab.name,
					icon: this.viewTabsConfig[tabType].icon,
					translationKey: this.viewTabsConfig[tabType].translationKey,
					hideInProduction: !!tab.meta.hideInProduction,
				})
			})
			return tabs
		},
		isProductionMode() {
			const productionModes = ['stage', 'prod']
			return productionModes.includes(import.meta.env.VITE_APP_ENV_MODE)
		},
		currentModule() {
			// item.meta.titleTranslationKey ? $t(item.meta.titleTranslationKey) : item.meta.title
			const currentModule = this.modules.find((module) => (module.name === this.currentModuleRouteName))
			if (!currentModule) return null
			return {
				title: currentModule.meta.titleTranslationKey ? this.$t(currentModule.meta.titleTranslationKey) : currentModule.meta.title,
				hasOtherModules: this.modules.length >= 2,
			}
		},
		currentTabView() {
			// item.meta.titleTranslationKey ? $t(item.meta.titleTranslationKey) : item.meta.title
			const currentTabView = this.tabRoutes.find((module) => (module.name === this.currentViewRouteName))
			if (!currentTabView) return null
			const tabType = this.getTabType(currentTabView.path)
			return {
				title: this.$t(this.viewTabsConfig[tabType].translationKey),
				icon: this.viewTabsConfig[tabType].icon,
				hasOtherTabViews: this.tabRoutes.length >= 2,
			}
		},
		moduleLinks() {
			const defaultClass = this.headerVariant === 'default' ? 'mx-1' : 'ma-1'
			return this.modules.map((item) => ({
				name: item.name,
				to: {
					name: item.meta.defaultTabView ? item.meta.defaultTabView : item.name,
					params: { companyId: this.selectedCompanyId },
				},
				meta: item.meta,
				text: item.meta.titleTranslationKey || item.meta.title ? (item.meta.titleTranslationKey ? this.$t(item.meta.titleTranslationKey) : item.meta.title) : item.path,
				active: item.name === this.currentModuleRouteName,
				loading: this.loadedRouterLink === (item.meta.defaultTabView ? item.meta.defaultTabView : item.name),
				class: item.meta.hideInProduction ? `${defaultClass} ao-text-600` : `${defaultClass}`,
				custom: 'navigationLink',
			})).filter((el) => (this.isProductionMode ? !el.meta.hideInProduction : true))
		},
		tabViewLinks() {
			return this.tabRoutes.map((tab) => {
				const tabType = this.getTabType(tab.path)
				const custom = this.headerVariant === 'default' ? 'tabLink' : 'navigationLink'
				const defaultClass = this.headerVariant === 'default' ? 'mx-1' : 'ma-1'
				return {
					name: tab.name,
					to: {
						name: tab.name,
						params: { companyId: this.selectedCompanyId },
					},
					meta: tab.meta,
					text: this.$t(this.viewTabsConfig[tabType].translationKey),
					prependIconName: this.viewTabsConfig[tabType].icon,
					active: this.currentViewRouteName === tab.name,
					loading: this.loadedRouterLink === tab.name,
					class: defaultClass,
					style: tab.meta.hideInProduction ? 'opacity: 0.3;' : '',
					custom,
					exact: true,
				}
			}).filter((el) => (this.isProductionMode ? !el.meta.hideInProduction : true))
		},
	},

	mounted() {
		eventBus.$on('navigationRouterLinkLoading', this.updateRouterLinkState)
		this.headerVariant = clientStorageLocal.get(LOCAL_STORAGE_APP_HEADER_VARIANT, 'default')
	},

	methods: {
		getTabType(path) {
			let type = ''
			switch (path) {
				case '':
					type = 'dashboard'
					break
				case 'analytics':
					type = 'analytics'
					break
				case 'dashboard':
					type = 'dashboard'
					break
				case 'terminal':
					type = 'terminal'
					break
				case 'table':
					type = 'table'
					break
				case 'map':
					type = 'map'
					break
				case 'schedule':
					type = 'schedule'
					break
				case 'timeline':
					type = 'timeline'
					break
				case 'calendar':
					type = 'calendar'
					break
				case 'comparison':
					type = 'comparison'
					break
				case 'list':
					type = 'list'
					break
				case 'monitor':
					type = 'monitor'
					break
				default:
					type = ''
					break
			}
			return type
		},

		changeHeaderVariant() {
			const newVariant = this.headerVariant === 'default' ? 'mini' : 'default'
			this.headerVariant = newVariant
			clientStorageLocal.add(LOCAL_STORAGE_APP_HEADER_VARIANT, newVariant)
		},

		updateRouterLinkState(payload) {
			// console.log('updateRouterLinkState', payload)
			if (payload.status === 'start') {
				this.loadedRouterLink = payload.name
			} else {
				this.loadedRouterLink = null
			}
		},
	},
}
</script>

<style lang="scss">
// тут потрібно поправити $primary
$primary: #3f73ab;
$black: #1d2126;
.app-base-header {
	// background: rgb(var(--v-theme-background));
	// &.first-app-bar.default-variant {
	// 	.v-toolbar__content {
	// 		padding-top: 16px;
	// 	}
	// }
	.spacer {
		height: 5px;
	}
	.second-app-bar {
		width: 100%;
		// &-menu{
		//    height: 30px;
		// }
		&-tabs {
			height: 45px;
		}
	}

	.v-tab-menu-selected {
		background-color: white !important;

		span.menu-tab-text {
			color: $black;
		}
	}
}

.app-bar-view-tabs {
	.mdi-arrow-right-drop-circle {
		color: $primary;
		font-size: 14px;
	}

	.mdi-arrow-left-drop-circle {
		color: $primary;
		font-size: 14px;
	}
	.v-toolbar__content {
		padding: 0 1em;
	}
	.v-slide-group {
		width: 100%;
	}
}
.app-bar-modules {
	.mdi-arrow-right-drop-circle {
		color: $primary;
		font-size: 14px;
	}

	.mdi-arrow-left-drop-circle {
		color: $primary;
		font-size: 14px;
	}
	.v-toolbar__content {
		padding: 0 1em;
	}
	.v-slide-group {
		width: 100%;
	}
}
.app-bar-tabs-block {
	padding-top: 2px;
	height: 33px;
}
</style>
